import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getFinancialProfile } from '@/selectors/user.selectors'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { PracticeType } from '@/constants/onboardingConstants'
import {
  useFetchResponse,
  useLoadedFlagValue,
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useViewerPlanSegment } from '@/features/user/settings/sections/plan-change/lib/helpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  SIGNUP_PATHS,
  PRACTICE_JOURNEY_SELECTIONS,
  practiceJourneyTitle,
} from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { useAnalyticsView } from '@/features/amplitude'
import { selectCanOptIntoPreviousTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { TypographyStyles } from '@/styles'
import { Text } from '@/ui'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import {
  fetchEligibleStripeProducts,
  FETCH_STRIPE_PRODUCTS_KEY,
  PlanTier,
} from '@/actions/settings/billingActions'
import {
  FETCH_PRODUCT_PERMISSIONS_KEY,
  getProductPermissions,
} from '@/actions/permissionActions'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { TierSelection } from './TierSelection'
import { selectProductDescription } from '@/features/user/signup/selectors'
import { usePayrollSignupPromo } from '../payroll-signup-promo.helpers'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'

export const LegacyChoosePlan = () => {
  const dispatch = useAppDispatch()

  const navigate = useNavigateWithPersistParams()
  const [loading, setLoading] = useState(false)
  const pageView = useAnalyticsView()
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const [searchParams] = useSearchParams()
  const freeTrialSearchParamValue = searchParams.get('trial')
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )
  const eligiblePlans = useFetchResponse(fetchEligibleStripeProducts)
  const eligibleLitePlans = useMemo(
    () =>
      eligiblePlans?.filter((plan) => plan.metadata?.tier === PlanTier.lite),
    [eligiblePlans]
  )
  const eligibleStandardPlans = useMemo(
    () =>
      eligiblePlans?.filter(
        (plan) =>
          plan.metadata?.tier === PlanTier.essential ||
          plan.metadata?.tier === PlanTier.premium
      ),
    [eligiblePlans]
  )
  const standardPlanTier = useMemo(
    () => eligibleStandardPlans?.[0]?.metadata?.tier,
    [eligibleStandardPlans]
  )
  const { payrollPromoTextForTier } = usePayrollSignupPromo()
  const litePlanIds = useMemo(
    () => eligibleLitePlans?.map((plan) => plan.id) || [],
    [eligibleLitePlans]
  )
  const litePlanPermissions = useFetchResponse(getProductPermissions, {
    productIds: litePlanIds,
  })
  const standardPlanIds = useMemo(
    () => eligibleStandardPlans?.map((plan) => plan.id) || [],
    [eligibleStandardPlans]
  )
  const standardPlanPermissions = useFetchResponse(getProductPermissions, {
    productIds: standardPlanIds,
  })
  const [planTier, setPlanTier] = useState<string | undefined>(
    localStorage.getItem('planTier') ?? undefined
  )
  const isLoadingPlanData = useReselector(selectIsFetchingForKeys, [
    FETCH_PRODUCT_PERMISSIONS_KEY,
    FETCH_STRIPE_PRODUCTS_KEY,
  ])
  const financialProfile = useReselector(getFinancialProfile)
  const isScorp =
    financialProfile?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s
  const isGroup = financialProfile?.practiceType === PracticeType.group
  const selectedLitePlan = planTier === PlanTier.lite
  const freeTrialEnabled = useFreeTrialEnabled(freeTrialSearchParamValue)
  const copyFromTier = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.planCopyFromTier,
    false
  )
  const viewerPlanSegment = useViewerPlanSegment()
  const standardPlanKey =
    standardPlanTier === PlanTier.premium ? 'premium' : 'essential'
  const litePlanDetails = useReselector(selectProductDescription, {
    sections: ['bookkeeping', 'reporting', 'taxes', 'support'],
    isLitePlanEnabled: true,
    permissions: litePlanPermissions?.[litePlanIds[0]],
    tier: PlanTier.lite,
    copyFromTier: Boolean(copyFromTier),
  })
  const standardPlanDetails = useReselector(selectProductDescription, {
    sections: ['taxes', 'scorp', 'group', 'support'],
    isLitePlanEnabled: true,
    permissions: standardPlanPermissions?.[standardPlanIds[0]],
    tier: standardPlanTier,
    copyFromTier: Boolean(copyFromTier),
    payrollPromoText: payrollPromoTextForTier(standardPlanTier),
  })

  // The Essential/Premium card already advertises 'Includes: Everything in
  // Lite Plan'. Strip any item from its sections that also appears under the
  // Lite card so we don't duplicate content (e.g., quarterly tax estimates,
  // S-corp election guidance).
  const dedupedStandardPlanDetails = useMemo(() => {
    if (!standardPlanDetails) return standardPlanDetails
    const liteItemTexts = new Set(
      litePlanDetails
        ?.filter((s) => s.isVisible)
        .flatMap((s) => s.details.map((d) => d.text)) ?? []
    )
    return standardPlanDetails.map((section) => {
      const filtered = section.details.filter((d) => !liteItemTexts.has(d.text))
      return {
        ...section,
        details: filtered,
        isVisible: section.isVisible && filtered.length > 0,
      }
    })
  }, [standardPlanDetails, litePlanDetails])

  useEffect(() => {
    pageView('sign up practice journey')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    // S-corps and group practices aren't offered Heard Lite, so they should
    // never land on this page. Redirect them to the pricing step if they do
    // (e.g. via direct URL or back-navigation from payment).
    if (isScorp || isGroup) {
      navigate(SIGNUP_PATHS.payment)
    }
  }, [isScorp, isGroup, navigate])

  const handleSubmit = () => {
    setLoading(true)
    let res: boolean | undefined
    if (planTier) {
      localStorage.setItem('planTier', planTier)
      res = true
    }
    setLoading(false)

    if (res) {
      // Heard Lite skips previous-year-tax step (no annual tax filing).
      // Trial codes do not skip it — they see the same steps as a paid signup.
      if (selectedLitePlan) {
        navigate(SIGNUP_PATHS.payment)
      } else {
        if (canOptIntoPreviousYearTaxes) {
          navigate(SIGNUP_PATHS.previousYearTax)
        } else {
          navigate(SIGNUP_PATHS.payment)
        }
      }
    }
  }

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onBack={() => navigate(SIGNUP_PATHS.aboutYourPractice)}
          onContinue={handleSubmit}
          continueDisabled={!planTier || loading}
          continueLoading={loading}
        />
      }
    >
      <div className="flex flex-col space-y-6">
        <span ref={scrollRef} />

        {freeTrialEnabled && <FreeTrialSignupBanner />}

        <Text variant={TypographyStyles.DISPLAY_MD}>
          Where are you in your private practice journey?
        </Text>

        <Text variant={TypographyStyles.BODY_MD}>
          Choose the option that is closest to your current practice.
        </Text>

        <div className="flex flex-col md:flex-row gap-6">
          <TierSelection
            {...PRACTICE_JOURNEY_SELECTIONS['lite']}
            setSelection={setPlanTier}
            selection={planTier}
            details={litePlanDetails}
            isStarterPlan
            isLoading={isLoadingPlanData}
          />
          <TierSelection
            {...PRACTICE_JOURNEY_SELECTIONS[standardPlanKey]}
            title={practiceJourneyTitle(standardPlanKey, viewerPlanSegment)}
            setSelection={setPlanTier}
            selection={planTier}
            details={dedupedStandardPlanDetails}
            isLoading={isLoadingPlanData}
          />
        </div>
      </div>
    </SignupPageLayout>
  )
}
